"use client";

import { SocialAvatar, SocialComposerField, SocialIcon } from "@webdo/community-ui";
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { usePortalTranslations } from "../i18n/client";
import { appendCommentPage, communityErrorKey, communityJson, communityRequest, type CommunityComment, type Comments } from "../lib/community";
import { CommunityReactions } from "./CommunityReactions";
import { usePortalPreferences } from "./PortalPreferences";
import styles from "./Community.module.css";

function CommentForm({ initialBody = "", reply, editing, onSubmit, onCancel }: {
  initialBody?: string; reply?: boolean; editing?: boolean; onSubmit: (body: string) => Promise<void>; onCancel?: () => void;
}) {
  const t = usePortalTranslations();
  const id = useId();
  const [body, setBody] = useState(initialBody);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState<ReturnType<typeof communityErrorKey> | null>(null);
  useEffect(() => {
    if (body === initialBody) return;
    const guard = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [body, initialBody]);
  return <form className={styles.commentForm} onSubmit={async (event) => {
    event.preventDefault(); if (submitting.current || !body.trim()) return;
    submitting.current = true;
    setBusy(true); setError(null);
    try { await onSubmit(body.trim()); setBody(""); } catch (failure) { setError(communityErrorKey(failure)); } finally { submitting.current = false; setBusy(false); }
  }}>
    <div className={styles.commentComposer}>
      {!editing ? <SocialAvatar name={t("community.you")} size={30} /> : null}
      <div className={styles.commentInput}><SocialComposerField id={id} variant="comment" label={t(reply ? "community.replyLabel" : "community.commentLabel")} placeholder={t(reply ? "community.replyPlaceholder" : "community.commentPlaceholder")} value={body} required maxLength={5000} disabled={busy} autoFocus={reply || editing} onChange={(event) => setBody(event.target.value)} /></div>
      <button className={styles.sendComment} type="submit" aria-label={t(busy ? "community.saving" : editing ? "community.save" : reply ? "community.sendReply" : "community.sendComment")} disabled={busy || !body.trim()}><SocialIcon name="send" /></button>
    </div>
    {error ? <p role="alert" className={styles.error}>{t(error)}</p> : null}
    {onCancel ? <button type="button" className={styles.cancelComment} disabled={busy} onClick={() => { if (body === initialBody || window.confirm(t("community.discard"))) onCancel(); }}>{t("community.cancel")}</button> : null}
  </form>;
}

function CommentItem({ comment, parent, canComment, loginHref, children, onReply, onUpdate }: {
  comment: CommunityComment; parent?: CommunityComment; canComment: boolean; loginHref: string; children?: ReactNode; onReply: (body: string, parentUuid: string) => Promise<void>; onUpdate: (comment: CommunityComment) => void;
}) {
  const t = usePortalTranslations();
  const { locale } = usePortalPreferences();
  const [mode, setMode] = useState<"reply" | "edit" | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<ReturnType<typeof communityErrorKey> | null>(null);
  async function remove() {
    if (!window.confirm(t("community.deleteComment"))) return;
    setDeleting(true); setError(null);
    try {
      await communityRequest<void>(`comments/${comment.uuid}`, { method: "DELETE" });
      onUpdate({ ...comment, body: "", authorName: null, deleted: true, canEdit: false, canDelete: false, reactions: [] });
      setMode(null);
    } catch (failure) { setError(communityErrorKey(failure)); } finally { setDeleting(false); }
  }
  return <article className={styles.comment} id={`comment-${comment.uuid}`}>
    <div className={styles.commentRow}>
      <SocialAvatar name={comment.deleted ? "" : comment.authorName || t("community.anonymous")} size={30} />
      <div className={styles.commentContent}>
        {comment.parentUuid ? <a href={`#comment-${comment.parentUuid}`} className={styles.replyContext}>{parent ? t("community.replyTo", { name: parent.authorName || t("community.anonymous") }) : t("community.parentComment")}</a> : null}
        {mode === "edit" ? <CommentForm initialBody={comment.body} editing onCancel={() => setMode(null)} onSubmit={async (body) => { const updated = await communityRequest<CommunityComment>(`comments/${comment.uuid}`, communityJson("PUT", { body })); onUpdate(updated); setMode(null); }} /> : <p className={`${styles.commentBody} ${comment.deleted ? styles.deleted : ""}`}>{!comment.deleted ? <strong>{comment.authorName || t("community.anonymous")}</strong> : null}{comment.deleted ? t("community.deleted") : comment.body}</p>}
        <div className={styles.commentMeta}>
          <time dateTime={comment.createdAt} title={new Intl.DateTimeFormat(locale === "cs" ? "cs-CZ" : "en-GB", { dateStyle: "medium", timeStyle: "short" }).format(new Date(comment.createdAt))}>{new Intl.DateTimeFormat(locale === "cs" ? "cs-CZ" : "en-GB", { day: "numeric", month: "short" }).format(new Date(comment.createdAt))}</time>
          {comment.updatedAt !== comment.createdAt && !comment.deleted ? <span>{t("community.edited")}</span> : null}
          {canComment && !comment.deleted ? <button type="button" disabled={deleting || mode !== null} onClick={() => setMode("reply")}>{t("community.reply")}</button> : null}
          {comment.canEdit ? <button type="button" disabled={deleting || mode !== null} onClick={() => setMode("edit")}>{t("community.edit")}</button> : null}
          {comment.canDelete ? <button type="button" disabled={deleting} onClick={() => void remove()}>{t("community.delete")}</button> : null}
        </div>
        {error ? <p className={styles.error} role="alert">{t(error)}</p> : null}
        {mode === "reply" ? <CommentForm reply onCancel={() => setMode(null)} onSubmit={async (body) => { await onReply(body, comment.uuid); setMode(null); }} /> : null}
      </div>
      {!comment.deleted ? <CommunityReactions key={comment.uuid + comment.updatedAt} targetType="comment" targetUuid={comment.uuid} authenticated={canComment} loginHref={loginHref} initialItems={comment.reactions} compact /> : null}
    </div>
    {children}
  </article>;
}

export function CommunityComments({ targetType, targetUuid, loginHref, onTotalChange }: { targetType: "post" | "discussion"; targetUuid: string; loginHref: string; onTotalChange?: (total: number) => void }) {
  const t = usePortalTranslations();
  const [data, setData] = useState<Comments | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ReturnType<typeof communityErrorKey> | null>(null);
  const pending = useRef(false);
  const pendingCommentUuids = useRef(new Set<string>());
  const path = `${targetType}/${targetUuid}/comments`;
  const load = useCallback(async (offset: number, signal?: AbortSignal) => {
    if (pending.current) return;
    pending.current = true; setBusy(true); setError(null);
    try {
      const page = await communityRequest<Comments>(`${path}?offset=${offset}&limit=50`, { signal });
      if (!signal?.aborted) {
        const unslotted = new Set(pendingCommentUuids.current);
        setData((current) => ({ ...page, items: offset === 0 ? page.items : appendCommentPage(current?.items ?? [], page.items, unslotted) }));
        for (const item of page.items) pendingCommentUuids.current.delete(item.uuid);
      }
    } catch (failure) { if (!signal?.aborted) setError(communityErrorKey(failure)); }
    finally { pending.current = false; if (!signal?.aborted) setBusy(false); }
  }, [path]);
  useEffect(() => {
    const controller = new AbortController();
    void load(0, controller.signal);
    return () => { controller.abort(); pending.current = false; };
  }, [load]);

  useEffect(() => { if (data) onTotalChange?.(data.total); }, [data, onTotalChange]);

  async function add(body: string, parentUuid: string | null) {
    const comment = await communityRequest<CommunityComment>(path, communityJson("POST", { body, parentUuid }));
    pendingCommentUuids.current.add(comment.uuid);
    setData((current) => current ? { ...current, items: [...current.items, comment], total: current.total + 1 } : current);
  }
  function update(comment: CommunityComment) {
    setData((current) => current ? { ...current, total: current.total - (comment.deleted && !current.items.find((item) => item.uuid === comment.uuid)?.deleted ? 1 : 0), items: current.items.map((item) => item.uuid === comment.uuid ? comment : item) } : current);
  }
  const items = data?.items ?? [];
  const byId = new Map(items.map((item) => [item.uuid, item]));
  const children = new Map<string, CommunityComment[]>();
  for (const item of items) if (item.parentUuid && byId.has(item.parentUuid)) children.set(item.parentUuid, [...(children.get(item.parentUuid) ?? []), item]);
  function renderComment(comment: CommunityComment, ancestors: Set<string>, depth = 0): ReactNode {
    if (ancestors.has(comment.uuid)) return null;
    const next = new Set([...ancestors, comment.uuid]);
    return <CommentItem key={comment.uuid} comment={comment} parent={comment.parentUuid ? byId.get(comment.parentUuid) : undefined} canComment={data?.canComment ?? false} loginHref={loginHref} onReply={add} onUpdate={update}>{children.get(comment.uuid)?.map((child) => <div key={child.uuid} className={depth < 2 ? styles.reply : undefined}>{renderComment(child, next, depth + 1)}</div>)}</CommentItem>;
  }
  return <section className={styles.comments} aria-label={t("community.comments")}>
    {data ? <>{data.canComment ? <CommentForm onSubmit={(body) => add(body, null)} /> : <a className={styles.loginHint} href={loginHref}>{t("community.loginComment")}</a>}
      <div className={styles.commentList}>{items.filter((item) => !item.parentUuid || !byId.has(item.parentUuid)).map((item) => renderComment(item, new Set()))}</div>
      {!items.length ? <p className={styles.empty}>{t("community.noComments")}</p> : null}</> : null}
    {error ? <div role="alert" className={styles.error}>{t(error)} <button type="button" className={styles.linkButton} disabled={busy} onClick={() => void load(data?.nextOffset ?? 0)}>{t("community.retry")}</button></div> : null}
    {busy ? <p role="status" className={styles.muted}>{t("community.loading")}</p> : data?.nextOffset != null ? <button type="button" className={styles.secondary} onClick={() => void load(data.nextOffset!)}>{t("community.moreComments")}</button> : null}
  </section>;
}
